import { types } from 'mobx-state-tree';

import ProductClass, { ProductClassType } from '../types/ProductClass';
import QuantityDiscountDisplayStyles from '../types/QuantityDiscountDisplayStyles';
import Category from './Category';
import Manufacturer from './Manufacturer';
import FullProductMatrix from './product/FullProductMatrix';
import FullProductMulti from './product/FullProductMulti';
import ProductPriceInfo from './ProductPriceInfo';

// Wishlist products need to be in some store
// otherwise getRoot-function doesn't work.
const ProductListProduct = types
  .model('ProductListProduct', {
    actual_code: types.string,
    availability_html: types.string,
    available_for_purchase: types.boolean,
    class: ProductClassType,
    collection: types.maybeNull(FullProductMatrix),
    hierarchy: types.maybeNull(types.array(Category)),
    id: types.string,
    image: types.maybeNull(types.string),
    link: types.string,
    main_category_id: types.maybeNull(types.number),
    main_section_id: types.maybeNull(types.number),
    manufacturer: types.maybeNull(Manufacturer),
    multi: types.maybeNull(FullProductMulti),
    multiproduct_id: types.string,
    multiproduct_title: types.maybeNull(types.string),
    name: types.string,
    product_id: types.string,
    stock_unit: types.string,
    variations: types.array(types.maybeNull(types.string)),
    package_size: types.maybeNull(types.number),
    price_info: types.maybeNull(ProductPriceInfo),
    quantity: types.maybeNull(types.number),
    total_price_info: types.maybeNull(ProductPriceInfo),
  })
  .views((self) => {
    const getTotalPrice = () => {
      return self.total_price_info?.getPrice(true) || null;
    };

    return {
      get analytics() {
        const totalPrice = getTotalPrice();

        return {
          quantity: self.quantity,
          totalPrice,
        };
      },
      get productCode() {
        return self.actual_code;
      },
      get sellInPackage() {
        return (
          self.package_size && self.package_size > 0 && self.package_size !== 1
        );
      },
      getQuantityStep() {
        return self.sellInPackage ? self.package_size : 1;
      },
      getDiscountInfo: (includeTax, quantityDiscountDisplayStyle) => {
        /*
         * TODO: Extend this model from core product model.
         */
        let savings = null;
        let discountAmount = null;
        let hasPriceRange =
          self.class === ProductClass.MULTI &&
          self.multi.hasPriceRange(includeTax);
        let hasDiscount = false;
        let minPrice = null;
        let maxPrice = null;

        if (!self.hasDiscount) {
          savings = 0;
          discountAmount = 0;
        }

        const baseDiscountInfo = () => {
          savings = self.price_info?.getSavings(includeTax);
          discountAmount = self.price_info?.discount_percentage;
          hasDiscount = self.price_info?.is_discount;
        };

        if (!self.getQuantityDiscounts().hasDiscounts) {
          baseDiscountInfo();
        }

        if (self.getQuantityDiscounts().hasDiscounts) {
          const quantityDiscount = self.quantity_discounts;

          switch (quantityDiscountDisplayStyle) {
            case QuantityDiscountDisplayStyles.LOWEST:
              discountAmount = self.getDiscountAmount(quantityDiscount.lowest);
              savings = quantityDiscount.lowest.getSavings(
                self.price_info?.getNormalPrice(includeTax),
                includeTax
              );
              hasDiscount = true;
              break;
            case QuantityDiscountDisplayStyles.RANGE:
              discountAmount = self.getDiscountAmount(quantityDiscount.lowest);
              hasDiscount = true;
              hasPriceRange = true;
              minPrice = quantityDiscount.lowest;
              maxPrice = quantityDiscount.highest;
              break;
            default:
              baseDiscountInfo();
              break;
          }
        }

        return {
          savings,
          discountAmount,
          hasDiscount,
          hasPriceRange,
          minPrice,
          maxPrice,
        };
      },
      getPrice(withTax) {
        return self.price_info ? self.price_info.getPrice(withTax) : 0;
      },
      getQuantityDiscounts: () => {
        return {
          hasDiscounts:
            self.quantity_discounts &&
            self.quantity_discounts.levels.length > 0,
          discounts: self.quantity_discounts,
        };
      },
    };
  });

export default ProductListProduct;
